import { useEffect, useState } from "react";
import { ApiError } from "../../api/client";
import type { Authorization, StaffEntry, StaffInput, StaffList } from "../../api/types";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { NewDeviceDialog } from "../printing/NewDeviceDialog";
import { holds, mayEdit, pills, RIGHTS, ROLE_ORDER, shortName } from "./rights";
import { StaffDialog } from "./StaffDialog";

interface Props {
  /** The code that opened Beheer (owner or manager); every change is made in this person's name. */
  authorization: Authorization;
}

/**
 * Beheer › Personeel & rechten, as in the prototype: who works here with role, rights (orange = an
 * exception for that person, red ✕ = taken away) and status; the default rights per role below.
 * A switch puts someone in or out of service (Furkan, 09/10/2026: "zoals een aan/uit schuifschakelaar"):
 * out of service signs in nowhere, and the name stays in old orders. The owner pairs a person's phone
 * from his row; someone who never did anything can be removed in his window.
 */
export function StaffScreen({ authorization }: Props) {
  const { api, report, staffRevision, toast } = useTill();
  const [list, setList] = useState<StaffList | null>(null);
  // everyone by default, those in service together on top (Furkan, 18/10/2026)
  const [showInactive, setShowInactive] = useState(true);
  const [open, setOpen] = useState<{ member: StaffEntry | null } | null>(null);
  const [pairing, setPairing] = useState<StaffEntry | null>(null);
  const [switching, setSwitching] = useState<string | null>(null);
  const editor = authorization.staff.role;
  const owner = editor === "owner";
  const text = t.staffAdmin;

  useEffect(() => {
    let current = true;
    api
      .request<StaffList>("GET", "/device/staff", { staffToken: authorization.token, retry: true })
      .then((answer) => {
        if (current) setList(answer);
      })
      .catch(report);
    return () => {
      current = false;
    };
  }, [api, authorization.token, report, staffRevision]);

  const replace = (saved: StaffEntry) => {
    setList(
      (current) =>
        current && {
          ...current,
          staff: current.staff.some((item) => item.id === saved.id)
            ? current.staff.map((item) => (item.id === saved.id ? saved : item))
            : [...current.staff, saved],
        },
    );
  };

  /** The switch: in or out of service at once, everything else of the person stays as it is. */
  const switchService = async (member: StaffEntry) => {
    const body: StaffInput = {
      name: member.name,
      role: member.role,
      phone: member.phone,
      active: !member.active,
      permissions: member.permissions,
      ...(owner && { paymentMethods: member.paymentMethods }),
    };
    setSwitching(member.id);
    try {
      const answer = await api.request<{ staff: StaffEntry }>("PUT", `/device/staff/${member.id}`, {
        staffToken: authorization.token,
        body,
      });
      replace(answer.staff);
      toast(answer.staff.active ? text.nowActive(answer.staff.name) : text.nowInactive(answer.staff.name));
    } catch (failure) {
      if (failure instanceof ApiError) toast(failure.message, "bad");
      else report(failure);
    } finally {
      setSwitching(null);
    }
  };

  if (list === null) return <div className="panel admin-section" aria-busy="true" />;
  const codeRights = RIGHTS.filter((right) => right.code)
    .map((right) => right.name.toLowerCase())
    .join(", ");
  const shown = list.staff
    .filter((member) => member.active || showInactive)
    .toSorted((a, b) => Number(b.active) - Number(a.active));

  return (
    <div className="panel admin-section">
      <h2>{text.title}</h2>
      {/* a phone: no long explanation above the list (Furkan, 18/10/2026) */}
      <p className="note pc-only">{text.intro(codeRights)}</p>
      <div className="rowacts">
        <button
          type="button"
          className="btn primary"
          onClick={() => {
            setOpen({ member: null });
          }}
        >
          {text.add}
        </button>
        <label className="inl">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(event) => {
              setShowInactive(event.target.checked);
            }}
          />{" "}
          {text.showInactive}
        </label>
      </div>
      <div className="tablewrap">
        <table className="stafftbl people">
          <thead>
            <tr>
              <th>{text.columns.name}</th>
              <th>{text.columns.role}</th>
              <th>{text.columns.code}</th>
              <th>{text.columns.rights}</th>
              <th>{text.columns.status}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {shown.map((member) => {
              const editable = mayEdit(editor, member);
              return (
                <tr key={member.id} className={member.active ? "" : "dim"}>
                  <td>
                    <b>{member.name}</b>
                    {member.phone && (
                      <>
                        <br />
                        <span className="note">{member.phone}</span>
                      </>
                    )}
                  </td>
                  <td>{t.roles[member.role]}</td>
                  <td className="num">{editable ? "••••" : "—"}</td>
                  <td>
                    {member.role === "owner" ? (
                      <span className="pill ok">{text.all}</span>
                    ) : (
                      pills(member).map(({ right, kind }) => (
                        <span
                          key={right.id}
                          className={`pill${kind === "granted" ? " warn" : kind === "removed" ? " bad" : ""}`}
                          title={
                            kind === "granted"
                              ? text.exceptionTitle
                              : kind === "removed"
                                ? text.removedTitle
                                : text.defaultTitle
                          }
                        >
                          {kind === "removed" ? `✕ ${shortName(right)}` : shortName(right)}
                        </span>
                      ))
                    )}
                  </td>
                  <td>
                    <span className="swrow">
                      {editable && (
                        <button
                          type="button"
                          className="sw"
                          role="switch"
                          aria-checked={member.active}
                          aria-label={text.inServiceOf(member.name)}
                          disabled={switching !== null}
                          onClick={() => void switchService(member)}
                        />
                      )}
                      <span className={`stp ${member.active ? "on" : "off"}`}>
                        {!member.active ? text.inactive : member.clockedIn ? text.clockedIn : text.active}
                      </span>
                    </span>
                  </td>
                  <td>
                    {editable ? (
                      <span className="rowacts tight">
                        <button
                          type="button"
                          className="btn"
                          aria-label={`${text.edit} ${member.name}`}
                          onClick={() => {
                            setOpen({ member });
                          }}
                        >
                          {text.edit}
                        </button>
                        {/* only the owner pairs a device (as in Printers & toestellen) */}
                        {owner && member.active && member.role !== "owner" && (
                          <button
                            type="button"
                            className="btn"
                            aria-label={text.pairOf(member.name)}
                            onClick={() => {
                              setPairing(member);
                            }}
                          >
                            {text.pair}
                          </button>
                        )}
                      </span>
                    ) : (
                      <span className="note">{text.ownerOnly}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <h3 className="sfh">{text.matrix}</h3>
      <div className="tablewrap">
        <table className="matrix">
          <thead>
            <tr>
              <th />
              {ROLE_ORDER.map((role) => (
                <th key={role}>{t.roles[role]}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {RIGHTS.map((right) => (
              <tr key={right.id}>
                <td>
                  {right.name}
                  {right.code && (
                    <>
                      {" "}
                      <span className="pill">{text.codePill}</span>
                    </>
                  )}
                </td>
                {ROLE_ORDER.map((role) => (
                  <td key={role} className="c">
                    {holds(right, list.roles[role]) ? "✓" : "—"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <StaffDialog
          authorization={authorization}
          roles={list.roles}
          member={open.member}
          onSaved={(saved) => {
            replace(saved);
            setOpen(null);
          }}
          onRemoved={(removed) => {
            setList(
              (current) => current && { ...current, staff: current.staff.filter((item) => item.id !== removed.id) },
            );
            toast(text.removed(removed.name));
            setOpen(null);
          }}
          onClose={() => {
            setOpen(null);
          }}
        />
      )}
      {pairing && (
        <NewDeviceDialog
          authorization={authorization}
          devices={[]}
          initial={{ type: "android", name: text.pairName(pairing.name) }}
          onClose={() => {
            setPairing(null);
          }}
        />
      )}
    </div>
  );
}
